import { useCallback, useEffect, useState } from "react";

/**
 * The one-off engine release notice on the Playground pages (ADR-0058, the
 * release note in `docs/records/2026-09-06_playground_engine_release_note.md`).
 *
 * Time-boxed and per-browser: the news is the 2026-08-22 cutover, so the card
 * stops showing itself on 2026-10-06 whatever a browser remembers, and a
 * dismissal is a `localStorage` convenience that survives republishes of the
 * page and reaches nobody else. The id in the stored value means a future
 * notice with a new id shows again without a key rename.
 *
 * Server render is always empty: this is not SEO content, and starting hidden
 * spares a browser that dismissed it the flash of a card it already closed.
 */
export const ENGINE_NOTICE_ID = "runner-2026-09";
/** 2026-10-06T00:00:00Z, exclusive. */
export const ENGINE_NOTICE_UNTIL_MS = Date.UTC(2026, 9, 6);

const KEY = "progsity:playground:engine-notice-dismissed";

export function isEngineNoticeActive(nowMs: number = Date.now()): boolean {
  return nowMs < ENGINE_NOTICE_UNTIL_MS;
}

export function loadEngineNoticeDismissed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(KEY) === ENGINE_NOTICE_ID;
  } catch {
    return false;
  }
}

export function persistEngineNoticeDismissed(): void {
  try {
    window.localStorage.setItem(KEY, ENGINE_NOTICE_ID);
  } catch {
    /* quota / private mode: the card comes back next visit, which is fine */
  }
}

export function useEngineNotice(): { visible: boolean; dismiss: () => void } {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(isEngineNoticeActive() && !loadEngineNoticeDismissed());
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    persistEngineNoticeDismissed();
  }, []);

  return { visible, dismiss };
}
